import { useId, useMemo, useState, type CSSProperties } from 'react'
import type { Question, ShopsWith } from '../../lib/firstRun'
import { inviteText } from '../../lib/getStarted'
import { itemKey } from '../../lib/normalize'
import { MAX_STAPLES, staplesFor, storeSuggestions } from '../../lib/starterItems'
import { Claim, Frame } from './Frame'

// The questions after setting up (lib/firstRun.ts questionsFor), one per
// screen, each changing something at once: who is on the list (how hard the
// tour pushes the invite), the staples (straight onto the list, each in its
// aisle), the stores (Start shopping already knows them) and the watch (No
// hides Get started's pairing step). Every one can be skipped. Then the list
// they made, and Open Matlist.

export interface PersonalizeProps {
  questions: Question[]
  /** The phone's locale: the staples' language and the stores' region. */
  locale: string
  householdName: string
  inviteCode: string
  /** Who joined a list rather than starting one: welcomed onto it, no invite. */
  joined?: boolean
  /** Names already on the list; their staples show as there. */
  onList: string[]
  /** The household's stores. */
  stores: string[]
  /** The list as it stands, for the last screen. */
  summary: { count: number; aisles: { name: string; tint: string }[] }
  /** The aisle colour a name would land in. */
  tintOf: (name: string) => string
  onAddItems: (names: string[]) => void
  onAddStores: (names: string[]) => void
  onWatch: (wears: boolean) => void
  onDone: (answers: { shopsWith?: ShopsWith; garmin?: boolean }, pairNow: boolean) => void
}

const MAX_STORES = 3
const WHO: { value: ShopsWith; label: string }[] = [
  { value: 'me', label: 'Just me' },
  { value: 'two', label: 'Two of us' },
  { value: 'family', label: 'The whole family' },
]
const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

export function PersonalizeFlow(p: PersonalizeProps) {
  const [at, setAt] = useState(0)
  const [answers, setAnswers] = useState<{ shopsWith?: ShopsWith; garmin?: boolean }>({})
  const steps = p.questions.length + 1
  const question = p.questions[at] as Question | undefined
  const next = () => setAt(a => a + 1)

  return (
    <Frame steps={steps} at={at} onSkip={question ? next : undefined} skipLabel="Skip"
      footer={!question && (
        <Payoff {...p} shopsWith={answers.shopsWith} garmin={answers.garmin}
          onDone={pair => p.onDone(answers, pair)} />
      )}>
      {question === 'who' && (
        <Choice title={['Who’s on', 'this list?']} body="Everyone on it sees every change as it happens, on their phone and their watch."
          options={WHO} onPick={v => { setAnswers(a => ({ ...a, shopsWith: v })); next() }} />
      )}
      {question === 'staples' && <Staples {...p} onNext={next} />}
      {question === 'stores' && <Stores {...p} onNext={next} />}
      {question === 'garmin' && (
        <Choice title={['Do you wear', 'a Garmin?']} body="Matlist runs on it: the list an aisle at a time, a tick per item, nothing to unlock."
          options={[{ value: true, label: 'Yes' }, { value: false, label: 'No' }]}
          onPick={v => { setAnswers(a => ({ ...a, garmin: v })); p.onWatch(v); next() }} />
      )}
      {!question && <PayoffHead joined={p.joined} householdName={p.householdName} />}
    </Frame>
  )
}

function Choice<T>({ title, body, options, onPick }: {
  title: string[]; body: string; options: { value: T; label: string }[]; onPick: (v: T) => void
}) {
  return (
    <div className="ml-fr__q">
      <Claim lines={title} />
      <p className="ml-fr__body">{body}</p>
      <div className="ml-fr__choices">
        {options.map(o => (
          <button key={o.label} type="button" className="ml-fr__choice" onClick={() => onPick(o.value)}>{o.label}</button>
        ))}
      </div>
    </div>
  )
}

function Staples({ locale, onList, tintOf, onAddItems, onNext }: PersonalizeProps & { onNext: () => void }) {
  const names = useMemo(() => staplesFor(locale), [locale])
  const there = useMemo(() => new Set(onList.map(itemKey)), [onList])
  const [picked, setPicked] = useState<string[]>([])
  const toggle = (name: string) => setPicked(old => (old.includes(name)
    ? old.filter(n => n !== name)
    : old.length < MAX_STAPLES ? [...old, name] : old))
  const go = () => { if (picked.length) onAddItems(picked); onNext() }
  return (
    <div className="ml-fr__q">
      <Claim lines={['What do you', 'always buy?']} />
      <p className="ml-fr__body">Tap a few. They go straight on your list, each in its aisle.</p>
      <ul className="ml-fr__chips">
        {names.map(name => {
          const on = there.has(itemKey(name))
          const isPicked = on || picked.includes(name)
          return (
            <li key={name}>
              <button type="button" className={`ml-fr__chip${isPicked ? ' is-picked' : ''}`} aria-pressed={isPicked} disabled={on}
                style={{ '--ml-tint': tintOf(name) } as CSSProperties} onClick={() => toggle(name)}>
                <span className="ml-dot" aria-hidden="true" />{name}
              </button>
            </li>
          )
        })}
      </ul>
      <FootButton onClick={go}>{picked.length ? `Add ${picked.length}` : 'Skip for now'}</FootButton>
    </div>
  )
}

function Stores({ locale, stores, onAddStores, onNext }: PersonalizeProps & { onNext: () => void }) {
  const [picked, setPicked] = useState<string[]>([])
  const [typed, setTyped] = useState('')
  const fieldId = useId()
  const suggested = useMemo(() => {
    const out = [...stores]
    for (const s of storeSuggestions(locale)) if (!out.some(o => sameName(o, s))) out.push(s)
    return out
  }, [locale, stores])
  const has = (name: string) => stores.some(s => sameName(s, name))
  const isPicked = (name: string) => has(name) || picked.some(s => sameName(s, name))
  const full = picked.length + stores.length >= MAX_STORES
  const toggle = (name: string) => setPicked(old => (old.some(s => sameName(s, name))
    ? old.filter(s => !sameName(s, name))
    : full ? old : [...old, name]))
  const add = () => {
    const name = typed.trim().replace(/\s+/g, ' ').slice(0, 40)
    if (name && !isPicked(name) && !full) setPicked(old => [...old, name])
    setTyped('')
  }
  const shown = [...suggested, ...picked.filter(s => !suggested.some(o => sameName(o, s)))]
  const go = () => { if (picked.length) onAddStores(picked); onNext() }
  return (
    <div className="ml-fr__q">
      <Claim lines={['Where do', 'you shop?']} />
      <p className="ml-fr__body">Pick up to three. Each store learns its own order, so the list walks the aisles with you.</p>
      <ul className="ml-fr__chips">
        {shown.map(name => (
          <li key={name}>
            <button type="button" className={`ml-fr__chip${isPicked(name) ? ' is-picked' : ''}`} aria-pressed={isPicked(name)}
              disabled={has(name) || (full && !isPicked(name))} onClick={() => toggle(name)}>{name}</button>
          </li>
        ))}
      </ul>
      <form className="ml-fr__field" onSubmit={e => { e.preventDefault(); add() }}>
        <label htmlFor={fieldId} className="ml-visually-hidden">Another store</label>
        <input id={fieldId} value={typed} onChange={e => setTyped(e.target.value)} placeholder="Another store…"
          autoComplete="off" enterKeyHint="done" disabled={full} />
        <button type="submit" className="ml-fr__add" disabled={!typed.trim() || full}>Add store</button>
      </form>
      <FootButton onClick={go}>{picked.length ? 'Continue' : 'Skip for now'}</FootButton>
    </div>
  )
}

/** A question's own button, at the foot like every first-run button. */
function FootButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <div className="ml-fr__qfoot">
      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={onClick}>{children}</button>
    </div>
  )
}

function PayoffHead({ joined, householdName }: { joined?: boolean; householdName: string }) {
  return (
    <div className="ml-fr__q">
      {joined ? <Claim lines={['You’re on', `${householdName}.`]} /> : <Claim lines={['Your list', 'is ready.']} />}
    </div>
  )
}

function Payoff({ summary, joined, shopsWith, garmin, householdName, inviteCode, stores, onDone }: Omit<PersonalizeProps, 'onDone'> & {
  shopsWith?: ShopsWith; garmin?: boolean; onDone: (pair: boolean) => void
}) {
  const [copied, setCopied] = useState(false)
  const invite = !joined && (shopsWith === 'two' || shopsWith === 'family')
  async function share() {
    const text = inviteText(householdName, inviteCode, `${window.location.origin}/`)
    if (navigator.share) {
      try { await navigator.share({ text }); return } catch (e) { if ((e as Error)?.name === 'AbortError') return }
    }
    try { await navigator.clipboard.writeText(text); setCopied(true) } catch { /* the code is on screen */ }
  }
  return (
    <div className="ml-fr__payoff">
      <div className="ml-fr__card" data-testid="payoff">
        <p className="ml-fr__count"><span className="ml-num">{summary.count}</span> to get</p>
        {summary.aisles.length > 0 && (
          <ul className="ml-fr__aisles">
            {summary.aisles.map(a => (
              <li key={a.name} style={{ '--ml-tint': a.tint } as CSSProperties}><span className="ml-dot" aria-hidden="true" />{a.name}</li>
            ))}
          </ul>
        )}
        {stores.length > 0 && (
          <p className="ml-fr__stores"><span className="ml-label">Shopping at</span>{stores.join(', ')}</p>
        )}
        {invite && (
          <div className="ml-fr__invite">
            <span className="ml-label">Invite code</span>
            <span className="ml-fr__code ml-num">{inviteCode}</span>
            <button type="button" className="ml-fr__add" onClick={share}>{copied ? 'Copied' : 'Invite'}</button>
          </div>
        )}
      </div>
      {garmin && (
        <button type="button" className="ml-btn ml-btn--secondary ml-btn--wide" onClick={() => onDone(true)}>Pair my watch</button>
      )}
      <button type="button" className="ml-btn ml-btn--primary ml-btn--wide" onClick={() => onDone(false)}>Open Matlist</button>
    </div>
  )
}
